import { groupByMonth, ratingLabel, showAccent, dateParts, computeHighlights } from "@/components/DiaryPoster";

/**
 * Draws the diary as a set of phone-shaped pictures (1080 x 1920 PNGs). It follows the PDF page (diary PDF design) closely:
 * dark page, burgundy hero with a gold edge, month ribbons, ticket-stub cards with the poster full height on the left.
 * Everything is drawn as canvas text, so nothing a person has written is ever treated as markup.
 * Posters are only drawn if their host allows it; otherwise the coloured placeholder with gold initials is used.
 */

/** A poster address that is safe to use: http(s) only, made absolute. Anything else is dropped. */
export function safePosterUrl(src, base) {
  if (!src || typeof src !== "string") return "";
  try {
    const u = new URL(src, base || (typeof window !== "undefined" && window.location && window.location.href) || "https://mytheatre.uk/");
    return u.protocol === "https:" || u.protocol === "http:" ? u.href : "";
  } catch { return ""; }
}

export const W = 1080;
export const PAGE_H = 1920;
const M = 36;                      // outer margin
const TOP = 112;                   // room for the brand line
const BOTTOM = 120;                // room for the footer
const ACCENT_W = 8;
const POSTER_W = 200;
const STUB_W = 140;
const TEXT_PAD = 28;
const CARD_W = W - 2 * M;
const BODY_W = CARD_W - ACCENT_W - POSTER_W - STUB_W - TEXT_PAD * 2;
const CARD_MIN = 300;

const C = {
  bg: "#0D0A0E", cream: "#FAF7F2", text: "#E8E2D8", muted: "#A89FA9", foot: "#8F8591",
  gold: "#C89B3C", goldLight: "#E6C071", border: "#4A3A1F",
};
const SERIF = `Georgia, "Times New Roman", serif`;
const SANS = `-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
const EMOJI = `, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji"`;
const PALETTES = [["#7a1020", "#2a0a12"], ["#4a2a6a", "#160d22"], ["#1f4a5a", "#0a1a20"], ["#6a4a12", "#1f1608"]];
const hash = (s) => { let h = 0; for (const c of s || "") h = (h * 31 + c.charCodeAt(0)) >>> 0; return h; };

export function initials(name) {
  const words = (name || "").replace(/[^\p{L}\p{N}\s]/gu, " ").split(/\s+/).filter(Boolean);
  const skip = new Set(["the", "a", "an", "of", "and"]);
  const main = words.filter((w) => !skip.has(w.toLowerCase()));
  return (main.length ? main : words).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}

const stars = (n) => (n ? "★".repeat(n) + "☆".repeat(5 - n) : "");
const len = (t) => [...t].length;

/** Width of `t` in the current font, with `sp` extra pixels after every character (letter-spacing without relying on ctx.letterSpacing). */
export const spacedWidth = (ctx, t, sp = 0) => ctx.measureText(t).width + sp * len(t);

/** Break text into lines that fit `maxW` with the ctx's current font; long unbroken words are split. At most `maxLines`, with "…". */
export function wrap(ctx, text, maxW, maxLines = 99, sp = 0) {
  const m = (t) => spacedWidth(ctx, t, sp);
  const out = [];
  for (const para of String(text ?? "").split(/\n/)) {
    const words = para.split(/\s+/).filter(Boolean);
    let cur = "";
    for (let w of words) {
      while (m(w) > maxW) {                               // a single word wider than the line
        let n = len(w);
        const chars = [...w];
        while (n > 1 && m(chars.slice(0, n).join("")) > maxW) n--;
        if (cur) { out.push(cur); cur = ""; }
        out.push(chars.slice(0, n).join("")); w = chars.slice(n).join("");
      }
      const t = cur ? cur + " " + w : w;
      if (m(t) <= maxW) cur = t; else { out.push(cur); cur = w; }
    }
    if (cur || !words.length) out.push(cur);
  }
  if (out.length > maxLines) {
    const kept = out.slice(0, maxLines);
    let last = [...kept[maxLines - 1]];
    while (last.length > 1 && m(last.join("") + "…") > maxW) last.pop();
    kept[maxLines - 1] = last.join("").replace(/[\s,.;:]+$/, "") + "…";
    return kept;
  }
  return out;
}

/** Draw text with extra space between characters, left / centre / right aligned at x. */
function spaced(ctx, text, x, y, sp, align = "left") {
  const total = spacedWidth(ctx, text, sp) - sp;
  let cx = align === "center" ? x - total / 2 : align === "right" ? x - total : x;
  const prev = ctx.textAlign;
  ctx.textAlign = "left";
  for (const ch of [...text]) { ctx.fillText(ch, cx, y); cx += ctx.measureText(ch).width + sp; }
  ctx.textAlign = prev;
}

/** The pieces of one card's text, in reading order, each knowing its own height. */
function cardText(ctx, e, includePrivate) {
  const blocks = [];
  const text = (t, font, color, lh, maxLines, gap, extra = {}) => {
    if (!t) return;
    ctx.font = font;
    const lines = wrap(ctx, t, BODY_W - (extra.indent || 0), maxLines);
    blocks.push({ kind: "text", font, color, lh, gap, lines, h: lines.length * lh + gap, ...extra });
  };
  text(e.show_name || "Untitled", `700 38px ${SERIF}`, C.cream, 46, 3, 10);
  text(e.venue, `400 26px ${SANS}`, C.muted, 34, 2, 12);
  if (e.rating) blocks.push({ kind: "rate", stars: stars(e.rating), label: ratingLabel(e.rating), h: 40 + 12 });
  text(e.verdict ? `“${e.verdict}”` : "", `italic 400 29px ${SERIF}`, C.cream, 40, 3, 12);
  if (e.tags?.length) {
    ctx.font = `500 22px ${SANS}`;
    const rows = []; let row = [], x = 0;
    for (const tg of e.tags) {
      const label = wrap(ctx, tg, BODY_W - 40, 1)[0];
      const w = ctx.measureText(label).width + 40;
      if (x + w > BODY_W && row.length) { rows.push(row); row = []; x = 0; }
      row.push({ label, w, x }); x += w + 10;
    }
    if (row.length) rows.push(row);
    blocks.push({ kind: "tags", rows, h: rows.length * 46 + 12 });
  }
  text(e.note, `400 27px ${SANS}`, C.text, 38, 6, 12);
  text(e.review, `400 27px ${SANS}`, C.text, 38, 8, 12);
  text(e.best_performance ? `Best performance: ${e.best_performance}` : "", `400 24px ${SANS}`, C.muted, 32, 2, 10);
  text(e.recommend_for ? `Great for: ${e.recommend_for}` : "", `400 24px ${SANS}`, C.muted, 32, 2, 10);
  if (e.would_see_again === true) text("👍 Would see again", `400 24px ${SANS}${EMOJI}`, C.muted, 32, 1, 10);
  if (e.would_see_again === false) text("Wouldn't see again", `400 24px ${SANS}`, C.muted, 32, 1, 10);
  if (includePrivate) {
    const where = [e.seat_area && `Seat: ${e.seat_area}`, e.went_with && `With: ${e.went_with}`].filter(Boolean).join("  ·  ");
    const priv = { priv: true, indent: 26 };
    text(where, `400 24px ${SANS}`, C.muted, 32, 2, 10, priv);
    if (e.cast_seen?.length) text(`Cast seen: ${e.cast_seen.map((c) => (c.role ? `${c.actor_name} (${c.role})` : c.actor_name)).join(", ")}`, `400 24px ${SANS}`, C.muted, 32, 3, 10, priv);
    text(e.favourite_moment ? `✨ ${e.favourite_moment}` : "", `italic 400 26px ${SERIF}${EMOJI}`, C.goldLight, 36, 3, 10, priv);
  }
  return blocks;
}

const blocksHeight = (blocks) => blocks.reduce((h, b) => h + b.h, 0);

/** Work out which items sit on which page. Returns [{ items: [{ kind, y, h, ... }] }]. */
export function layoutDiary(ctx, { entries, title, scope, includePrivate }) {
  const pages = [{ items: [] }];
  let y = TOP;
  const put = (item) => { pages[pages.length - 1].items.push({ ...item, y }); y += item.h; };
  const room = (h) => y + h <= PAGE_H - BOTTOM;
  const newPage = () => { pages.push({ items: [] }); y = TOP; };

  // Cover: the hero
  ctx.font = `700 68px ${SERIF}`;
  const titleLines = wrap(ctx, title, CARD_W - 100, 3);
  const rated = entries.filter((e) => e.rating);
  const avg = rated.length ? (rated.reduce((a, e) => a + e.rating, 0) / rated.length).toFixed(1) : "";
  const theatres = new Set(entries.map((e) => e.venue).filter(Boolean)).size;
  const summary = [`🎟️ ${entries.length} ${entries.length === 1 ? "show" : "shows"}`, avg && `⭐ ${avg} average`, theatres && `🏛️ ${theatres} ${theatres === 1 ? "theatre" : "theatres"}`].filter(Boolean).join("   ·   ");
  const lastTitle = 172 + (titleLines.length - 1) * 80;
  put({ kind: "hero", h: lastTitle + 168 + 60, titleLines, summary, scope, lastTitle });
  y += 24;

  if (entries.length > 1) {
    const h = computeHighlights(entries, new Date().getFullYear());
    const defs = [
      h.top && ["⭐", "Top rated", h.top.name],
      h.fav ? ["🏛️", "Favourite theatre", `${h.fav.name} · ${h.fav.visits} visits`] : theatres ? ["🏛️", "Theatres visited", String(theatres)] : null,
      ["📅", "Shows this year", String(h.thisYear)],
    ].filter(Boolean);
    const gap = 20, bw = (CARD_W - gap * (defs.length - 1)) / defs.length;
    const boxes = defs.map(([icon, label, value]) => {
      ctx.font = `600 19px ${SANS}`;
      const labelLines = wrap(ctx, label.toUpperCase(), bw - 40 - 40, 2, 3);
      ctx.font = `700 32px ${SERIF}`;
      const valueLines = wrap(ctx, value, bw - 40, 3);
      return { icon, labelLines, valueLines };
    });
    const bh = Math.max(...boxes.map((b) => 28 + b.labelLines.length * 28 + 12 + b.valueLines.length * 40 + 24));
    put({ kind: "highlights", h: bh, boxes, bw, gap });
    y += 8;
  }

  if (!entries.length) { put({ kind: "empty", h: 120 }); return pages; }

  for (const g of groupByMonth(entries)) {
    let started = false;
    for (const e of g.items) {
      const blocks = cardText(ctx, e, includePrivate);
      const h = Math.max(CARD_MIN, blocksHeight(blocks) + 48 - 8);
      const need = (started ? 0 : 56 + 44) + h + 22;     // the month ribbon travels with its first card
      if (!room(need)) newPage();
      if (!started) { y += 20; put({ kind: "month", h: 56, label: g.label, count: g.items.length }); y += 22; started = true; }
      put({ kind: "card", h, entry: e, blocks });
      y += 22;
    }
  }
  return pages;
}

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawCover(ctx, img, x, y, w, h) {
  const s = Math.max(w / img.width, h / img.height);
  const dw = img.width * s, dh = img.height * s;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

/** A gradient running along a CSS-style 135 degree diagonal across the box. */
function diag(ctx, x, y, w, h, stops) {
  const g = ctx.createLinearGradient(x, y, x + w, y + h);
  stops.forEach(([o, c]) => g.addColorStop(o, c));
  return g;
}

function drawHero(ctx, it) {
  const x = M, y = it.y, w = CARD_W, h = it.h;
  rr(ctx, x, y, w, h, 44);
  ctx.fillStyle = diag(ctx, x, y, w, h, [[0, "#7a1020"], [0.55, "#2a1620"], [1, "#0D0A0E"]]); ctx.fill();
  ctx.save(); rr(ctx, x, y, w, h, 44); ctx.clip();
  const top = ctx.createLinearGradient(x, 0, x + w, 0); top.addColorStop(0, "rgba(200,155,60,0)"); top.addColorStop(0.5, C.gold); top.addColorStop(1, "rgba(200,155,60,0)");
  ctx.fillStyle = top; ctx.fillRect(x, y, w, 10); ctx.restore();
  rr(ctx, x, y, w, h, 44); ctx.strokeStyle = "rgba(200,155,60,0.45)"; ctx.lineWidth = 2; ctx.stroke();
  const cx = W / 2;
  ctx.textAlign = "center";
  ctx.fillStyle = C.goldLight; ctx.font = `700 24px ${SANS}${EMOJI}`; spaced(ctx, "🎭 THEATRE DIARY", cx, y + 84, 8, "center");
  ctx.fillStyle = C.cream; ctx.font = `700 68px ${SERIF}`;
  it.titleLines.forEach((l, i) => ctx.fillText(l, cx, y + it.lastTitle - (it.titleLines.length - 1 - i) * 80));
  const ty = y + it.lastTitle;
  ctx.fillStyle = C.gold; ctx.font = `400 32px ${SERIF}`; spaced(ctx, "✦ ✦ ✦", cx, ty + 66, 22, "center");
  ctx.fillStyle = "rgba(255,248,230,0.82)"; ctx.font = `500 29px ${SANS}${EMOJI}`; ctx.fillText(it.summary, cx, ty + 126);
  ctx.fillStyle = C.muted; ctx.font = `400 24px ${SANS}`; ctx.fillText(it.scope, cx, ty + 168);
}

function drawHighlights(ctx, it) {
  it.boxes.forEach((b, i) => {
    const bx = M + i * (it.bw + it.gap), y = it.y;
    rr(ctx, bx, y, it.bw, it.h, 28);
    ctx.fillStyle = diag(ctx, bx, y, it.bw, it.h, [[0, "rgba(200,155,60,0.12)"], [1, "#161118"]]); ctx.fill();
    ctx.strokeStyle = "rgba(200,155,60,0.3)"; ctx.lineWidth = 2; ctx.stroke();
    ctx.textAlign = "left";
    ctx.font = `400 22px ${SANS}${EMOJI}`; ctx.fillStyle = C.muted; ctx.fillText(b.icon, bx + 20, y + 42);
    ctx.font = `600 19px ${SANS}`;
    b.labelLines.forEach((l, j) => spaced(ctx, l, bx + 20 + 40, y + 42 + j * 28, 3));
    ctx.font = `700 32px ${SERIF}`; ctx.fillStyle = C.goldLight;
    const vy = y + 28 + b.labelLines.length * 28 + 12 + 30;
    b.valueLines.forEach((l, j) => ctx.fillText(l, bx + 20, vy + j * 40));
  });
}

function drawMonth(ctx, it) {
  const y = it.y;
  ctx.font = `700 30px ${SERIF}`;
  const tw = ctx.measureText(it.label).width + 32 + 40;
  ctx.beginPath();                                           // ribbon: flat on the left, round on the right
  ctx.moveTo(M, y); ctx.lineTo(M + tw - 28, y); ctx.arcTo(M + tw, y, M + tw, y + 28, 28); ctx.arcTo(M + tw, y + 56, M + tw - 28, y + 56, 28); ctx.lineTo(M, y + 56); ctx.closePath();
  const g = ctx.createLinearGradient(M, 0, M + tw, 0); g.addColorStop(0, "#7a1020"); g.addColorStop(1, "#9E1B28"); ctx.fillStyle = g; ctx.fill();
  ctx.textAlign = "left"; ctx.fillStyle = C.cream; ctx.fillText(it.label, M + 32, y + 39);
  const cnt = `${it.count} ${it.count === 1 ? "show" : "shows"}`;
  ctx.font = `400 24px ${SANS}`; ctx.textAlign = "right"; ctx.fillStyle = C.muted; ctx.fillText(cnt, W - M, y + 37);
  const cw = ctx.measureText(cnt).width, x1 = M + tw + 24, x2 = W - M - cw - 24;
  if (x2 > x1) { const lg = ctx.createLinearGradient(x1, 0, x2, 0); lg.addColorStop(0, "rgba(200,155,60,0.45)"); lg.addColorStop(1, "rgba(200,155,60,0)"); ctx.fillStyle = lg; ctx.fillRect(x1, y + 27, x2 - x1, 2); }
}

function drawCard(ctx, it, images) {
  const e = it.entry, h = it.h, x = M, y = it.y, w = CARD_W, R = 32;
  rr(ctx, x, y, w, h, R); ctx.fillStyle = diag(ctx, x, y, w, h, [[0, "#161118"], [1, "#1d1319"]]); ctx.fill();
  ctx.save(); rr(ctx, x, y, w, h, R); ctx.clip();
  ctx.fillStyle = showAccent(e.show_name || ""); ctx.fillRect(x, y, ACCENT_W, h);                       // coloured left edge
  // poster, full height
  const px = x + ACCENT_W, [a, b] = PALETTES[hash(e.show_name) % PALETTES.length];
  const pg = ctx.createLinearGradient(px, y, px + POSTER_W, y + h); pg.addColorStop(0, a); pg.addColorStop(1, b);
  ctx.fillStyle = pg; ctx.fillRect(px, y, POSTER_W, h);
  const img = images.get(safePosterUrl(e.image_url));
  if (img) drawCover(ctx, img, px, y, POSTER_W, h);
  else {
    rr(ctx, px + 12, y + 12, POSTER_W - 24, h - 24, 12); ctx.strokeStyle = "rgba(200,155,60,0.5)"; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = C.goldLight; ctx.font = `700 54px ${SERIF}`; spaced(ctx, initials(e.show_name), px + POSTER_W / 2, y + h / 2 + 18, 3, "center");
  }
  // ticket stub
  const sx = x + w - STUB_W;
  ctx.fillStyle = "rgba(0,0,0,0.25)"; ctx.fillRect(sx, y, STUB_W, h);
  ctx.restore();
  rr(ctx, x, y, w, h, R); ctx.strokeStyle = C.border; ctx.lineWidth = 2; ctx.stroke();
  ctx.strokeStyle = "rgba(200,155,60,0.45)"; ctx.setLineDash([8, 8]); ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(sx, y + 2); ctx.lineTo(sx, y + h - 2); ctx.stroke(); ctx.setLineDash([]);
  const dp = dateParts(e.date), cx = sx + STUB_W / 2, cy = y + h / 2;
  ctx.textAlign = "center";
  ctx.fillStyle = C.gold; ctx.font = `700 22px ${SANS}`; spaced(ctx, dp.month, cx, cy - 34, 4, "center");
  ctx.fillStyle = C.cream; ctx.font = `700 60px ${SERIF}`; ctx.fillText(dp.day, cx, cy + 24);
  ctx.fillStyle = C.muted; ctx.font = `400 22px ${SANS}`; ctx.fillText(dp.year, cx, cy + 62);

  // text
  const tx = px + POSTER_W + TEXT_PAD;
  let ty = y + 24, privTop = null, privBottom = null;
  ctx.textAlign = "left";
  for (const bl of it.blocks) {
    if (bl.kind === "text") {
      ctx.font = bl.font; ctx.fillStyle = bl.color;
      const ix = tx + (bl.indent || 0);
      bl.lines.forEach((l, i) => ctx.fillText(l, ix, ty + bl.lh * (i + 1) - Math.round(bl.lh * 0.26)));
      if (bl.priv) { if (privTop === null) privTop = ty + 4; privBottom = ty + bl.lines.length * bl.lh - 2; }
    } else if (bl.kind === "rate") {
      ctx.font = `400 29px ${SANS}`; ctx.fillStyle = C.gold; spaced(ctx, bl.stars, tx, ty + 32, 2);
      const sw = spacedWidth(ctx, bl.stars, 2);
      ctx.font = `400 24px ${SANS}`; ctx.fillStyle = C.goldLight; ctx.fillText(bl.label, tx + sw + 14, ty + 31);
    } else if (bl.kind === "tags") {
      ctx.font = `500 22px ${SANS}`;
      bl.rows.forEach((row, r) => row.forEach((p) => {
        const py = ty + r * 46;
        rr(ctx, tx + p.x, py + 2, p.w, 38, 19); ctx.strokeStyle = "rgba(200,155,60,0.5)"; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = C.goldLight; ctx.fillText(p.label, tx + p.x + 20, py + 29);
      }));
    }
    ty += bl.h;
  }
  if (privTop !== null) {                                      // dashed gold rule beside the private details
    ctx.strokeStyle = "rgba(200,155,60,0.6)"; ctx.setLineDash([6, 6]); ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(tx + 3, privTop); ctx.lineTo(tx + 3, privBottom); ctx.stroke(); ctx.setLineDash([]);
  }
}

function drawItem(ctx, it, images) {
  ctx.textBaseline = "alphabetic";
  if (it.kind === "hero") drawHero(ctx, it);
  else if (it.kind === "highlights") drawHighlights(ctx, it);
  else if (it.kind === "month") drawMonth(ctx, it);
  else if (it.kind === "card") drawCard(ctx, it, images);
  else if (it.kind === "empty") { ctx.textAlign = "center"; ctx.fillStyle = C.muted; ctx.font = `italic 400 30px ${SERIF}`; ctx.fillText("No diary entries to show.", W / 2, it.y + 60); }
}

function drawPage(ctx, page, idx, total, images, generated, includePrivate) {
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, PAGE_H);
  ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
  ctx.fillStyle = C.gold; ctx.font = `400 28px ${SERIF}`; spaced(ctx, "✦  MY THEATRE  ✦", W / 2, 76, 10, "center");
  for (const it of page.items) drawItem(ctx, it, images);
  const fy = PAGE_H - 78;
  ctx.textAlign = "center";
  ctx.fillStyle = C.gold; ctx.font = `400 26px ${SERIF}`; spaced(ctx, "✦ ✦ ✦", W / 2, fy - 14, 16, "center");
  ctx.fillStyle = C.foot; ctx.font = `400 22px ${SANS}`;
  ctx.fillText(`mytheatre.uk  ·  ${generated}  ·  ${idx + 1} of ${total}`, W / 2, fy + 22);
  if (includePrivate && idx === total - 1) ctx.fillText("Includes private details. Keep these to yourself if you don't want them shared.", W / 2, fy + 52);
}

/** Load posters that the browser will let us draw. Anything that fails or takes too long is simply left out. */
export async function loadPosters(entries, { timeout = 6000, loader } = {}) {
  const urls = [...new Set(entries.map((e) => safePosterUrl(e.image_url)).filter(Boolean))];
  const load = loader || ((u) => new Promise((res) => {
    const im = new Image();
    const timer = setTimeout(() => res(null), timeout);
    im.crossOrigin = "anonymous";
    im.onload = () => { clearTimeout(timer); res(im.naturalWidth ? im : null); };
    im.onerror = () => { clearTimeout(timer); res(null); };
    im.src = u;
  }));
  const out = new Map();
  for (let i = 0; i < urls.length; i += 6) {
    const batch = urls.slice(i, i + 6);
    const got = await Promise.all(batch.map(load));
    batch.forEach((u, j) => got[j] && out.set(u, got[j]));
  }
  return out;
}

const toBlob = (canvas) => new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("Could not make the picture"))), "image/png"));

/** Returns { blobs: Blob[], pages: number, posters: number }. */
export async function renderDiaryImages({ entries, title, scope, includePrivate, generated, images, loader }) {
  const probe = document.createElement("canvas").getContext("2d");
  const pages = layoutDiary(probe, { entries, title, scope, includePrivate });
  const posters = images || (await loadPosters(entries, { loader }));
  const blobs = [];
  for (let i = 0; i < pages.length; i++) {
    const cv = document.createElement("canvas");
    cv.width = W; cv.height = PAGE_H;
    drawPage(cv.getContext("2d"), pages[i], i, pages.length, posters, generated, includePrivate);
    blobs.push(await toBlob(cv));
  }
  return { blobs, pages: pages.length, posters: posters.size };
}